import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Mail, Phone, Search, Leaf, ArrowUpDown, ArrowUp, ArrowDown, Filter, ChevronDown, ChevronUp, X, Briefcase, MapPin } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'

interface SubcontractorWithCount {
  id: string
  name: string
  email: string
  phone: string | null
  company: string | null
  location: string | null
  org_number: string | null
  color: string
  is_active: boolean
  notes: string | null
  address_street: string | null
  address_postal_code: string | null
  address_city: string | null
  address_country: string | null
  created_at: string
  updated_at: string
  activeAssignmentCount: number
  reportingCount: number
}

const SUBCONTRACTORS_FILTER_STORAGE_KEY = 'subcontractors-advanced-filters'

interface SubcontractorFilters {
  showAdvancedFilters: boolean
  selectedStatuses: string[]
  selectedCompanies: string[]
  selectedCities: string[]
  itemsPerPage: number
}

const getSavedFilters = (): SubcontractorFilters | null => {
  try {
    const saved = sessionStorage.getItem(SUBCONTRACTORS_FILTER_STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.error('Error loading saved filters:', e)
  }
  return null
}

export function AdminSubcontractors() {
  const navigate = useNavigate()
  const [subcontractors, setSubcontractors] = useState<SubcontractorWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<string>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Advanced filter state with sessionStorage restoration
  const savedFilters = getSavedFilters()
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(savedFilters?.showAdvancedFilters ?? false)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(savedFilters?.selectedStatuses ?? [])
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(savedFilters?.selectedCompanies ?? [])
  const [selectedCities, setSelectedCities] = useState<string[]>(savedFilters?.selectedCities ?? [])
  const [itemsPerPage, setItemsPerPage] = useState<number>(savedFilters?.itemsPerPage ?? 20)

  // Search within filters
  const [companySearch, setCompanySearch] = useState('')
  const [citySearch, setCitySearch] = useState('')

  // Persist filters to sessionStorage
  useEffect(() => {
    const filters: SubcontractorFilters = {
      showAdvancedFilters,
      selectedStatuses,
      selectedCompanies,
      selectedCities,
      itemsPerPage
    }
    sessionStorage.setItem(SUBCONTRACTORS_FILTER_STORAGE_KEY, JSON.stringify(filters))
  }, [showAdvancedFilters, selectedStatuses, selectedCompanies, selectedCities, itemsPerPage])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Load subcontractors from Supabase with assignment counts
  const loadSubcontractors = async () => {
    try {
      setLoading(true)

      // Fetch subcontractors
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading subcontractors:', error)
        toast.error('Kunde inte ladda trädgårdsmästare: ' + error.message)
        return
      }

      // Fetch assignment_subcontractors with tg_status and assignment status to calculate counts
      const { data: assignmentData } = await supabase
        .from('assignment_subcontractors')
        .select('subcontractor_id, tg_status, assignments(status)')

      // Calculate counts per subcontractor
      const activeCountMap: Record<string, number> = {}
      const reportingCountMap: Record<string, number> = {}

      const excludedStatuses = ['slutford', 'avbruten']
      // V2: TG needs to report if tg_status is 'ej_rapporterad' or 'komplettering'
      const needsReportingStatuses = ['ej_rapporterad', 'komplettering']

      assignmentData?.forEach(item => {
        if (item.subcontractor_id) {
          const assignmentStatus = (item.assignments as any)?.status
          const tgStatus = item.tg_status

          // Count active assignments (excluding slutford and avbruten)
          if (assignmentStatus && !excludedStatuses.includes(assignmentStatus)) {
            activeCountMap[item.subcontractor_id] = (activeCountMap[item.subcontractor_id] || 0) + 1
          }

          // Count where TG needs to submit/complete report (V2 tg_status)
          if (tgStatus && needsReportingStatuses.includes(tgStatus)) {
            reportingCountMap[item.subcontractor_id] = (reportingCountMap[item.subcontractor_id] || 0) + 1
          }
        }
      })

      // Transform data to match our interface
      const transformedSubcontractors: SubcontractorWithCount[] = data?.map(sub => ({
        id: sub.id,
        name: sub.name,
        email: sub.email,
        phone: sub.phone,
        company: sub.company,
        location: sub.location,
        org_number: sub.org_number,
        color: sub.color,
        is_active: sub.is_active,
        notes: sub.notes,
        address_street: sub.address_street,
        address_postal_code: sub.address_postal_code,
        address_city: sub.address_city,
        address_country: sub.address_country,
        created_at: sub.created_at,
        updated_at: sub.updated_at,
        activeAssignmentCount: activeCountMap[sub.id] || 0,
        reportingCount: reportingCountMap[sub.id] || 0
      })) || []

      setSubcontractors(transformedSubcontractors)
    } catch (error) {
      console.error('Error loading subcontractors:', error)
      toast.error('Kunde inte ladda trädgårdsmästare')
    } finally {
      setLoading(false)
    }
  }

  // Load subcontractors on component mount
  useEffect(() => {
    loadSubcontractors()
  }, [])

  // Extract unique companies from subcontractors
  const uniqueCompanies = useMemo(() => {
    const companies = subcontractors
      .map(s => s.company)
      .filter((company): company is string => company !== null && company.trim() !== '')
    return [...new Set(companies)].sort((a, b) => a.localeCompare(b, 'sv'))
  }, [subcontractors])

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    if (!companySearch) return uniqueCompanies
    return uniqueCompanies.filter(company =>
      company.toLowerCase().includes(companySearch.toLowerCase())
    )
  }, [uniqueCompanies, companySearch])

  // Extract unique cities from subcontractors
  const uniqueCities = useMemo(() => {
    const cities = subcontractors
      .map(s => s.address_city)
      .filter((city): city is string => city !== null && city.trim() !== '')
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'sv'))
  }, [subcontractors])

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return uniqueCities
    return uniqueCities.filter(city =>
      city.toLowerCase().includes(citySearch.toLowerCase())
    )
  }, [uniqueCities, citySearch])

  // Status options
  const statusOptions = [
    { key: 'active', name: 'Aktiv' },
    { key: 'inactive', name: 'Inaktiv' }
  ]

  // Count active filters
  const activeFilterCount = [
    selectedStatuses.length > 0,
    selectedCompanies.length > 0,
    selectedCities.length > 0
  ].filter(Boolean).length

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedStatuses([])
    setSelectedCompanies([])
    setSelectedCities([])
    setCompanySearch('')
    setCitySearch('')
  }

  // Filter subcontractors based on search term and advanced filters
  const filteredSubcontractors = useMemo(() => {
    return subcontractors.filter(sub => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.phone && sub.phone.includes(searchTerm)) ||
        (sub.company && sub.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sub.address_city && sub.address_city.toLowerCase().includes(searchTerm.toLowerCase()))

      // Status filter
      const matchesStatus = selectedStatuses.length === 0 ||
        (selectedStatuses.includes('active') && sub.is_active) ||
        (selectedStatuses.includes('inactive') && !sub.is_active)

      // Company filter
      const matchesCompany = selectedCompanies.length === 0 ||
        (sub.company && selectedCompanies.includes(sub.company))

      // City filter
      const matchesCity = selectedCities.length === 0 ||
        (sub.address_city && selectedCities.includes(sub.address_city))

      return matchesSearch && matchesStatus && matchesCompany && matchesCity
    })
  }, [subcontractors, searchTerm, selectedStatuses, selectedCompanies, selectedCities])

  // Sort subcontractors
  const sortedSubcontractors = useMemo(() => {
    return [...filteredSubcontractors].sort((a, b) => {
      let aValue: any = a[sortField as keyof SubcontractorWithCount]
      let bValue: any = b[sortField as keyof SubcontractorWithCount]

      // Handle null values
      if (aValue === null) aValue = ''
      if (bValue === null) bValue = ''

      if (typeof aValue === 'string') aValue = aValue.toLowerCase()
      if (typeof bValue === 'string') bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredSubcontractors, sortField, sortDirection])

  // Apply items per page limit
  const displayedSubcontractors = useMemo(() => {
    if (itemsPerPage === 0) return sortedSubcontractors // 0 = Show all
    return sortedSubcontractors.slice(0, itemsPerPage)
  }, [sortedSubcontractors, itemsPerPage])

  // Navigate to subcontractor and store filtered IDs for prev/next navigation
  const navigateToSubcontractor = (subcontractorId: string) => {
    // Store the current filtered subcontractor IDs for navigation
    const subcontractorIds = sortedSubcontractors.map(s => s.id)
    sessionStorage.setItem('filtered-subcontractor-ids', JSON.stringify(subcontractorIds))
    navigate(`/admin/subcontractors/${subcontractorId}`)
  }

  // Navigate to assignments filtered by subcontractor
  const navigateToSubcontractorAssignments = (e: React.MouseEvent, subcontractorId: string) => {
    e.stopPropagation() // Prevent row click
    // Set filter state in sessionStorage for assignments page
    const assignmentFilters = {
      showAdvancedFilters: true,
      dateFrom: '',
      dateTo: '',
      selectedStatuses: [],
      selectedSubcontractors: [subcontractorId],
      selectedCustomers: [],
      selectedCities: []
    }
    sessionStorage.setItem('assignmentFilters', JSON.stringify(assignmentFilters))
    navigate('/admin/assignments')
  }

  // Navigate to assignments needing reporting for this subcontractor
  const navigateToSubcontractorReporting = (e: React.MouseEvent, subcontractorId: string) => {
    e.stopPropagation() // Prevent row click
    // Filter on TG + statuses where reporting is needed (pagaende, granskning)
    const assignmentFilters = {
      showAdvancedFilters: true,
      dateFrom: '',
      dateTo: '',
      selectedStatuses: ['pagaende', 'granskning'],
      selectedSubcontractors: [subcontractorId],
      selectedCustomers: [],
      selectedCities: []
    }
    sessionStorage.setItem('assignmentFilters', JSON.stringify(assignmentFilters))
    navigate('/admin/assignments')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar trädgårdsmästare...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100">Trädgårdsmästare</h1>
        <button
          onClick={() => navigate('/admin/subcontractors/new')}
          className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Lägg till</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Sök trädgårdsmästare..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
        {/* Filters row */}
        <div className="flex items-center gap-2">
          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
              activeFilterCount > 0
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'border-nature-300 dark:border-nature-600 bg-[#faf9f7] dark:bg-nature-700 text-nature-700 dark:text-nature-200 hover:bg-nature-50 dark:hover:bg-nature-600'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Avancerade filter</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
            {showAdvancedFilters ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-md bg-[#faf9f7] dark:bg-nature-700 text-nature-900 dark:text-nature-100"
          >
            <option value={10}>10 st</option>
            <option value={20}>20 st</option>
            <option value={50}>50 st</option>
            <option value={100}>100 st</option>
            <option value={0}>Alla</option>
          </select>
          <span className="text-xs text-nature-500 dark:text-nature-400 whitespace-nowrap">
            ({filteredSubcontractors.length})
          </span>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  Status
                  {selectedStatuses.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedStatuses.length})</span>
                  )}
                </label>
                {selectedStatuses.length > 0 && (
                  <button
                    onClick={() => setSelectedStatuses([])}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {statusOptions.map(status => (
                  <label key={status.key} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status.key])
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.key))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      status.key === 'active'
                        ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                    }`}>
                      {status.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Company Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <Briefcase className="inline h-4 w-4 mr-1" />
                  Företag
                  {selectedCompanies.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCompanies.length})</span>
                  )}
                </label>
                {selectedCompanies.length > 0 && (
                  <button
                    onClick={() => { setSelectedCompanies([]); setCompanySearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {filteredCompanies.map(company => (
                  <label key={company} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCompanies.includes(company)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCompanies([...selectedCompanies, company])
                        } else {
                          setSelectedCompanies(selectedCompanies.filter(c => c !== company))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{company}</span>
                  </label>
                ))}
                {filteredCompanies.length === 0 && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 italic p-1">Inga företag hittades</p>
                )}
              </div>
            </div>

            {/* City Multi-select */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Ort
                  {selectedCities.length > 0 && (
                    <span className="ml-1 text-xs text-primary-600">({selectedCities.length})</span>
                  )}
                </label>
                {selectedCities.length > 0 && (
                  <button
                    onClick={() => { setSelectedCities([]); setCitySearch(''); }}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 flex items-center gap-0.5"
                  >
                    <X className="h-3 w-3" />
                    Rensa
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Sök..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="input-field text-sm w-full mb-1"
              />
              <div className="max-h-32 overflow-y-auto space-y-1 border border-nature-200 dark:border-nature-600 rounded-md p-2 bg-white dark:bg-nature-700">
                {filteredCities.map(city => (
                  <label key={city} className="flex items-center gap-2 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-600 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(city)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCities([...selectedCities, city])
                        } else {
                          setSelectedCities(selectedCities.filter(c => c !== city))
                        }
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-nature-300"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-200">{city}</span>
                  </label>
                ))}
                {filteredCities.length === 0 && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 italic p-1">Inga orter hittades</p>
                )}
              </div>
            </div>
          </div>

          {/* Clear filters button */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-600 flex justify-end">
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              >
                <X className="h-4 w-4" />
                Rensa alla filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Subcontractors List */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
          {displayedSubcontractors.map((subcontractor) => (
            <div
              key={subcontractor.id}
              onClick={() => navigateToSubcontractor(subcontractor.id)}
              className="p-4 cursor-pointer active:bg-nature-100 dark:active:bg-nature-700"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full border border-nature-200 dark:border-nature-600"
                    style={{ backgroundColor: subcontractor.color }}
                  />
                  <div>
                    <span className="font-medium text-nature-900 dark:text-nature-100">{subcontractor.name}</span>
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-medium ${
                      subcontractor.is_active
                        ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                    }`}>
                      {subcontractor.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {subcontractor.reportingCount > 0 ? (
                    <button
                      onClick={(e) => navigateToSubcontractorReporting(e, subcontractor.id)}
                      className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-full transition-colors"
                      title="Visa uppdrag som behöver rapporteras"
                    >
                      {subcontractor.reportingCount}
                    </button>
                  ) : (
                    <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                  )}
                  {subcontractor.activeAssignmentCount > 0 ? (
                    <button
                      onClick={(e) => navigateToSubcontractorAssignments(e, subcontractor.id)}
                      className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-full transition-colors"
                      title="Visa aktiva uppdrag för denna trädgårdsmästare"
                    >
                      {subcontractor.activeAssignmentCount}
                    </button>
                  ) : (
                    <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                  )}
                </div>
              </div>
              {(subcontractor.company || subcontractor.address_city || subcontractor.address_postal_code) && (
                <div className="text-sm text-nature-600 dark:text-nature-400 mb-1">
                  {subcontractor.company}
                  {subcontractor.company && (subcontractor.address_postal_code || subcontractor.address_city) && ' • '}
                  {subcontractor.address_postal_code}
                  {subcontractor.address_postal_code && subcontractor.address_city && ' '}
                  {subcontractor.address_city}
                </div>
              )}
              <div className="flex flex-wrap gap-3 text-xs text-nature-500 dark:text-nature-400">
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {subcontractor.email}
                </span>
                {subcontractor.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {subcontractor.phone}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-nature-50 dark:bg-nature-700">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Namn
                    {sortField === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    E-post
                    {sortField === 'email' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center gap-1">
                    Telefon
                    {sortField === 'phone' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('company')}
                >
                  <div className="flex items-center gap-1">
                    Företag
                    {sortField === 'company' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('address_postal_code')}
                >
                  <div className="flex items-center gap-1">
                    Postnr
                    {sortField === 'address_postal_code' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('address_city')}
                >
                  <div className="flex items-center gap-1">
                    Ort
                    {sortField === 'address_city' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('activeAssignmentCount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Uppdrag
                    {sortField === 'activeAssignmentCount' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider cursor-pointer hover:bg-nature-100 dark:hover:bg-nature-600"
                  onClick={() => handleSort('reportingCount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Rapportering
                    {sortField === 'reportingCount' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#faf9f7] dark:bg-nature-800 divide-y divide-nature-200 dark:divide-nature-700">
              {displayedSubcontractors.map((subcontractor) => (
                <tr
                  key={subcontractor.id}
                  onClick={() => navigateToSubcontractor(subcontractor.id)}
                  className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors duration-200 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: subcontractor.color }}
                      />
                      <div>
                        <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                          {subcontractor.name}
                        </div>
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                          subcontractor.is_active
                            ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                            : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                        }`}>
                          {subcontractor.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {subcontractor.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {subcontractor.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {subcontractor.company || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {subcontractor.address_postal_code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-nature-600 dark:text-nature-400">
                    {subcontractor.address_city || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {subcontractor.activeAssignmentCount > 0 ? (
                      <button
                        onClick={(e) => navigateToSubcontractorAssignments(e, subcontractor.id)}
                        className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-full transition-colors"
                        title="Visa aktiva uppdrag för denna trädgårdsmästare"
                      >
                        {subcontractor.activeAssignmentCount}
                      </button>
                    ) : (
                      <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {subcontractor.reportingCount > 0 ? (
                      <button
                        onClick={(e) => navigateToSubcontractorReporting(e, subcontractor.id)}
                        className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-full transition-colors"
                        title="Visa uppdrag som behöver rapporteras"
                      >
                        {subcontractor.reportingCount}
                      </button>
                    ) : (
                      <span className="text-sm text-nature-400 dark:text-nature-500">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {displayedSubcontractors.length === 0 && !loading && (
        <div className="text-center py-12">
          <Leaf className="h-12 w-12 text-nature-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
            {searchTerm || activeFilterCount > 0 ? 'Inga trädgårdsmästare hittades' : 'Inga trädgårdsmästare än'}
          </h3>
          <p className="text-nature-600 dark:text-nature-400">
            {searchTerm || activeFilterCount > 0 ? 'Prova att ändra söktermen eller filtren' : 'Lägg till din första trädgårdsmästare för att komma igång'}
          </p>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Rensa alla filter
            </button>
          )}
        </div>
      )}
    </div>
  )
}
